<template>
  <Tile class="opc-zm-ac-did">
    <Foldable v-model="isExpanded" :title="title" isRemoveWrapAriaLabelledby>
      <template #head>
        <div class="opc-zm-base-card__head">
          <div class="opc-zm-base-card__head-left">
            <img class="opc-zm-base-card__logo" :src="DIDIcon" alt="Dedicated Dial-In Numbers" tabindex="-1" />
          </div>
          <div class="opc-zm-base-card__head-right">
            <h2 class="opc-zm-base-card__name text-capitalize">
              {{ title }}
              <i class="zm-icon-success main-icon" aria-hidden="true" />
            </h2>
            <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!isExpanded">
              <p class="opc-zm-base-card__bill-info-item">{{ DIDBillInfo }}</p>
            </div>
          </div>
        </div>
      </template>

      <div class="opc-zm-ac-did__main">
        <p class="opc-zm-ac-did__main__desc">{{ $t('billing.tollfree.dedicated_dial_in_number.desc') }}</p>
        <div class="opc-zm-ac-did__main__number-row">
          <InputNumberWithPopover
            :value="DIDNumber"
            :label="'Dedicated Dial-In Numbers Select the number of numbers'"
            size="large"
            controls-position="both"
            :min="min"
            :max="max"
            :decrease-tips="decreaseTips"
            :increaseTips="increaseTips"
            :unit="`${$t('billing.planform.unit.number')} x ${$money(selectedDIDPlan.amount)}${$t(
              'billing.common.per_billing_cycle.monthly',
            )}`"
            :disabled="disableEditNumber"
            @change="handleDIDNumberChange"
            :data-tracking="{ buyInteraction: 'dedicated dial-in number numbers' }"
          />
        </div>
        <div v-if="showRemoveBtn" class="opc-zm-base-card__main__footer">
          <zm-tooltip :disabled='!disableDeleteTips' placement='top'>
            <template #content>
              <div v-html='disableDeleteTips'></div>
            </template>
            <div>
              <zm-button
                data-testid="checkout-button-79"
                :disabled='disableDeleteTips'
                type="link"
                ghost
                @click="removePlanByKey(DID_PLAN_KEY)"
                :aria-label="'remove Dedicated Dial-In Numbers plan'"
              >
                {{ $t('billing.common.remove') }}
              </zm-button>
            </div>
          </zm-tooltip>
        </div>
      </div>
    </Foldable>
  </Tile>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { get } from 'lodash';
import Tile from '@/components/tile.vue';
import Foldable from '@/components/foldable.vue';
import DIDIcon from '@/assets/plans/dedicated_dial_in_number.svg';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import plansMixin from '@/plans/mx/plan';
import { BILLING_CYCLE, contactUsPath, DEFAULT_MAX_HOST, DEFAULT_MIN_HOST, planTypes } from '@/common/constants';
import { getDIDPlanConfig, isDIDPlan } from '../common/index';
import { flowTypes } from '@/common/service/page-config';

const DID_PLAN_KEY = 'TOLLFREE$DID';

export default {
  mixins: [plansMixin],

  components: {
    Tile,
    Foldable,
    InputNumberWithPopover,
  },

  data() {
    return {
      isExpanded: true,
      title: this.$t('billing.tollfree.dedicated_dial_in_number.title'),
      DIDIcon,
      DID_PLAN_KEY,
      planType: planTypes.TOLLFREE$DID,
    };
  },

  computed: {
    ...mapGetters([
      'planMapFlat',
      'getSelectedPlans',
      'currentMeetingAndBundleLicense',
      'getCurrentBasePlanInfo',
      'boughtPlans',
      'subscriptions',
    ]),
    DIDPlanConfig() {
      return getDIDPlanConfig(this.boughtPlans);
    },
    isCancelled() {
      return this.DIDPlanConfig.isCancelled;
    },
    hasCancellationRecord() {
      return this.DIDPlanConfig.hasCancellationRecord;
    },
    disableEditNumber() {
      return this.isCancelled || this.hasCancellationRecord;
    },
    disableEdit() {
      return this.hasCancellationRecord && !this.isCancelled;
    },
    selectedACPlans() {
      return this.getSelectedPlans.filter((plan) => plan.type === planTypes.TOLLFREE);
    },
    selectedDIDPlans() {
      return this.getSelectedPlans.filter(isDIDPlan);
    },
    selectedDIDPlan() {
      return this.selectedDIDPlans[0];
    },
    DIDBillInfo({ selectedDIDPlan: plan }) {
      if (plan.billing_cycle === BILLING_CYCLE.MONTHLY) {
        const unit = this.$t('billing.planform.unit.number');
        const perUnit = this.$t('billing.planform.singular_unit.number');
        const billingCycle = this.$t('billing.common.per_billing_cycle.monthly');
        return `${plan.cartInfo.selected_number} ${unit} • ${this.$money(plan.amount)}${billingCycle}/${perUnit}`;
      }
      return '';
    },
    DIDNumber() {
      return this.selectedDIDPlan.cartInfo.selected_number;
    },
    isStandardPrice() {
      const isEditFlow = this.$context.flow === flowTypes.edit;
      return (isEditFlow && !this.hasAccountDiscount) ? this.selectedDIDPlan?.isStandardPrice : true;
    },
    min() {
      const disableDownsell = get(window.newBillingConfig, 'downsellData.disableDownsell', false);
      if ((disableDownsell || !this.isStandardPrice) && this.$context.is.edit) {
        return this.DIDPlanConfig.selectedNumber;
      }
      return DEFAULT_MIN_HOST;
    },
    max() {
      if (this.selectedACPlans.length > 0 && this.currentMeetingAndBundleLicense > 0) {
        return this.currentMeetingAndBundleLicense;
      }
      return DEFAULT_MAX_HOST;
    },
    increaseTips() {
      if (!this.disableEditNumber && this.DIDNumber >= this.max) {
        return this.$t('billing.tollfree.dedicated_dial_in_number.exceed_tip');
      }
      return '';
    },
    decreaseTips() {
      return '';
    },
    showRemoveBtn() {
      return !this.disableEdit && this.isShowRemoveBtn;
    },
    disableDeleteTips() {
      const hasBought = this.boughtPlans.find((p) => p.plan_code === this.selectedDIDPlan?.plan_code);
      if ((hasBought && this.basicConfig?.isDirectSales) || hasBought?.ratePlans?.length > 1) {
        return this.$t('billing.cancel_disable_contact_us', {
          url: contactUsPath,
        })
      }
      return null;
    },
  },

  watch: {
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(
          `${this.title} accordion`,
          `${val ? 'edit' : 'collapse'}`,
        );
      },
    },
    selectedACPlans: {
      immediate: true,
      handler(selectedACPlans) {
        // If the AC plan is removed the DID plan should be removed
        if (selectedACPlans.length < 1) {
          this.removePlanByKey(DID_PLAN_KEY);
        }
      },
    },
  },

  created() {
    this.initDIDPlan();
  },

  methods: {
    ...mapMutations(['updateSelectPlan']),
    initDIDPlan() {
      if (this.selectedDIDPlan) return;
      // add DID plan to selected
      const plans = this.planMapFlat.filter(isDIDPlan);
      const monthlyPlan = plans.find((plan) => plan.billing_cycle === BILLING_CYCLE.MONTHLY) || {};
      if (monthlyPlan.plan_code) {
        this.updateSelectPlan({
          plan_code: monthlyPlan.plan_code,
          cartInfo: {
            selected_number: this.disableEditNumber ? this.DIDPlanConfig.selectedNumber : DEFAULT_MIN_HOST,
          },
        });
      }
    },
    handleDIDNumberChange(number) {
      if (number >= this.min || number <= this.max) {
        this.updateSelectPlan({
          plan_code: this.selectedDIDPlan.plan_code,
          cartInfo: { selected_number: number },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.opc-zm-ac-did {
  &__main {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #dfdfe4;
    &__desc {
      color: #00053d;
    }
    &__number-row {
      margin-top: 24px;
    }
  }
}
</style>
